// Excel'den sayım alma: şablonu indir → doldur → yükle → önizle → uygula.
// Eşleşen satırların sayılan TOPLAMI Excel'deki değer olur (fark ayrı giriş olarak yazılır).
import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { fmtQty, stockCountService, type StockCountDetail, type StockCountLine } from './stockCountService';
import { exportCountTemplate, matchImportRows, readImportFile, type ImportPlan } from './stockCountExcel';

interface Props {
  open: boolean;
  count: StockCountDetail;
  lines: StockCountLine[];
  onClose: () => void;
  onDone: () => void;
}

const ImportCountExcelDialog: React.FC<Props> = ({ open, count, lines, onClose, onDone }) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState('');
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failures, setFailures] = useState<string[]>([]);
  const [finished, setFinished] = useState(false);

  const reset = () => {
    setFileName(''); setPlan(null); setError(null); setProgress(null); setFailures([]); setFinished(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  const close = () => { if (progress && !finished) return; reset(); onClose(); };

  const pick = async (file?: File | null) => {
    if (!file) return;
    reset();
    setFileName(file.name);
    setReading(true);
    try {
      const rows = await readImportFile(file);
      setPlan(matchImportRows(rows, lines));
    } catch (e: any) {
      setError(e?.message || 'Dosya okunamadı.');
    } finally {
      setReading(false);
    }
  };

  const apply = async () => {
    if (!plan) return;
    const jobs: { label: string; run: () => Promise<unknown> }[] = [
      ...plan.matched.map((m) => ({
        label: m.line.itemName || m.line.barcode || m.line.id,
        run: () => stockCountService.setTotal(count.id, m.line.id, m.qty),
      })),
      ...plan.byBarcode.map((r) => ({
        label: `${r.barcode} (satır ${r.rowNo})`,
        run: () => stockCountService.addEntry(count.id, {
          barcode: r.barcode, quantity: r.qty, entryQuantity: r.qty, method: 'MANUAL',
        }),
      })),
    ];
    const fails: string[] = [];
    setProgress({ done: 0, total: jobs.length });
    for (let i = 0; i < jobs.length; i += 1) {
      try {
        await jobs[i].run();
      } catch (e: any) {
        fails.push(`${jobs[i].label}: ${e?.message || 'kaydedilemedi'}`);
      }
      setProgress({ done: i + 1, total: jobs.length });
    }
    setFailures(fails);
    setFinished(true);
    onDone();
  };

  const busy = reading || (!!progress && !finished);

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Excel'den sayım al</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <Alert severity="info">
          Önce şablonu indirin, "Sayılan" sütununu doldurup buraya yükleyin. Eşleşen ürünlerin sayılan toplamı Excel'deki
          değer olur. Boş bırakılan satırlara dokunulmaz. Başka bir dosyada "Barkod" ya da "Stok Kodu" ile "Sayılan"
          (veya "Miktar") sütunları yeterlidir.
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportCountTemplate({ ...count, lines })} disabled={busy}>
            Şablonu indir
          </Button>
          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()} disabled={busy}>
            Dosya seç
          </Button>
          <input ref={fileRef} type="file" hidden accept=".xlsx" onChange={(e) => pick(e.target.files?.[0])} />
          {fileName && <Typography variant="body2" color="text.secondary">{fileName}</Typography>}
          {reading && <CircularProgress size={18} />}
        </Box>
        {error && <Alert severity="error">{error}</Alert>}

        {plan && !finished && (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2"><b>{plan.matched.length}</b> ürün eşleşti</Typography>
              {plan.byBarcode.length > 0 && <Typography variant="body2"><b>{plan.byBarcode.length}</b> barkod listede yok (kapsam dışı / tanınmayan olarak eklenir)</Typography>}
              {plan.skipped > 0 && <Typography variant="body2" color="text.secondary">{plan.skipped} satır boş, atlanacak</Typography>}
              {plan.invalid.length > 0 && <Typography variant="body2" color="error.main"><b>{plan.invalid.length}</b> satır alınamayacak</Typography>}
            </Box>
            {plan.matched.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ürün</TableCell>
                    <TableCell align="right">Şu an sayılan</TableCell>
                    <TableCell align="right">Excel'deki</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.matched.slice(0, 50).map((m) => (
                    <TableRow key={m.line.id}>
                      <TableCell>{m.line.itemName}{m.line.lotNo ? ` · ${m.line.lotNo}` : ''}</TableCell>
                      <TableCell align="right">{m.line.countedQty == null ? 'sayılmadı' : fmtQty(m.line.countedQty)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtQty(m.qty)} {m.line.unit || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {plan.matched.length > 50 && <Typography variant="caption" color="text.secondary">… ve {plan.matched.length - 50} ürün daha</Typography>}
            {plan.invalid.length > 0 && (
              <Alert severity="warning">
                {plan.invalid.slice(0, 10).map((x) => <div key={x.rowNo}>Satır {x.rowNo}: {x.reason}</div>)}
                {plan.invalid.length > 10 && <div>… ve {plan.invalid.length - 10} satır daha</div>}
              </Alert>
            )}
          </>
        )}

        {progress && (
          <Box>
            <LinearProgress variant="determinate" value={progress.total ? (progress.done / progress.total) * 100 : 100} />
            <Typography variant="caption" color="text.secondary">{progress.done} / {progress.total}</Typography>
          </Box>
        )}
        {finished && (
          failures.length === 0
            ? <Alert severity="success">Excel'deki sayımlar kaydedildi.</Alert>
            : (
              <Alert severity="warning">
                {progress!.total - failures.length} kayıt alındı, {failures.length} kayıt alınamadı:
                {failures.slice(0, 10).map((f) => <div key={f}>{f}</div>)}
              </Alert>
            )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>{finished ? 'Kapat' : 'Vazgeç'}</Button>
        {!finished && (
          <Button variant="contained" onClick={apply}
            disabled={busy || !plan || (plan.matched.length === 0 && plan.byBarcode.length === 0)}>
            Sayımlara işle
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportCountExcelDialog;
